/**
 * The request-list row marker (#1069 Story 2.2; FR53, UX-DR6, UX-DR8, UX-DR9,
 * UX-DR10; D5, superseded in part on 2026-09-11).
 *
 * The third and outermost level of the one unread signal: the thread card, the
 * request header's toggle, and this — a row on the list. With email gone from
 * the feature (#1529) it is THE ONLY WAY anyone notices a reply without opening
 * the request.
 *
 * ⚠️ A STATUS THAT CAN ALSO BE A DOOR. Story 2.2's D5 made this a status and
 * not a control (the row opens the request; the toggle is one tap away). The
 * owner reversed that on 2026-09-11 after seeing it on the list: pressing the
 * marker opens the request WITH the discussion showing, because the marker
 * is the thing that told you there is something to read. So: with `onPress`
 * the marker is a BUTTON, labelled "Open discussion" / "Open discussion,
 * unread", and the screen stops the press reaching the row (the row's own
 * press would open the request with the panel closed, and both would fire on
 * web). Without `onPress` it is what D5 said, an image the row's label
 * includes. `ViewRequests` always passes the handler; the image form remains
 * for any surface that has nowhere to go.
 *
 * ⚠️ NOTHING UNLESS `hasDiscussion === true`. Absent flags mean the server
 * degraded the page (NFR54), the row is an offline `o-` draft that never
 * reached the server, or a cached row from before the flags existed; all draw
 * nothing. `false` draws nothing. Only `true` draws the glyph — the same
 * `MessageSquare` the header toggle draws (UX-DR6), at the row's own size and
 * colour (16px, `colors.textSecondary`, matching Copy beside it) rather than
 * the toggle's.
 *
 * ⚠️ THE DOT OVERHANGS THE GLYPH'S TOP-RIGHT CORNER (owner change 2026-09-23).
 * This paragraph previously said the opposite -- that the dot is a sibling IN
 * FLOW, trailing the glyph, and must never be positioned over the corner,
 * because Android clips children that extend past a rounded parent.
 *
 * ⚠️ AND THE FIRST REWRITE OF THIS PARAGRAPH WAS ALSO WRONG, in a way worth
 * keeping on the record (found at code review the same day). It claimed
 * `glyphBox` -- un-rounded, no `overflow` -- is "what stops Android clipping the
 * overhang", and that this is how `DiscussionToggle` solves it. Both halves are
 * false. Read the render: `glyphBox` is the ONLY child of the touchable, and the
 * touchable is what carries the caller's radius (`actionButton`,
 * `borderRadius: 6`). The dot is therefore a DESCENDANT of the rounded box, and
 * an un-rounded box in between changes nothing about where a clip happens.
 * `DiscussionToggle` is genuinely a different structure: its dot is a sibling of
 * the BUTTON, outside the rounded box entirely. That option is not open here,
 * because the caller owns the outer style and the component cannot hoist a
 * sibling out of it.
 *
 * ⚠️ SO WHAT ACTUALLY KEEPS THE DOT UN-CLIPPED IS THE PADDING, AND NOTHING
 * ELSE. The dot is `UNREAD_DOT_SIZE` (12) pulled `DOT_OVERHANG_TOP` (3) up and
 * `DOT_OVERHANG_RIGHT` (5) across from the glyph, and the two hosts pad by 8
 * (`actionButton`) and 6 (`cardActionIcon`), so it stays inside the touchable's
 * padding box and never reaches its rounded edge. The binding case is 5 against
 * 6: ONE POINT of room, on the card. `view-requests-actions-column-floor.test.ts`
 * guards exactly that inequality, against paddings IMPORTED from the screen.
 *
 * ⚠️ THE GUARD MODELS PADDING, NOT RADIUS, and a corner clips on an ARC. A
 * radius large enough to reach inward past the padding would cut the dot with
 * every assertion still green; at the shipped `borderRadius` of 4 and 6 the dot
 * is clear, but marginally so on the card. Ledgered; only the Android walk can
 * settle it.
 *
 * `glyphBox`'s real and only job is to be the POSITIONING CONTEXT, so `top` and
 * `right` resolve against the 16 px glyph instead of the 32 px button. Without
 * it the same offsets would land the dot somewhere else entirely.
 *
 * The dot is out of flow, so it adds nothing to the marker's laid-out width --
 * which is why the request list's actions-column floor dropped from 88 to 72 in
 * the same change, and why the marker's tappable width dropped with it (48 -> 32
 * on the table, 44 -> 28 on the card; both still clear SC 2.5.8's 24). Ledgered.
 *
 * ⚠️ NO `elevation`, DELIBERATELY -- and `zIndex` is not doing much either.
 * `DiscussionToggle` carries `elevation: 6` because on Android elevation, not
 * JSX order, decides which of two siblings paints on top, and its dot has to
 * beat an ELEVATED `ToolbarActionButton`. Nothing here is elevated and the dot
 * is the later sibling, so document order already paints it above the glyph on
 * all three platforms; the `zIndex: 1` below is belt-and-braces, not the
 * mechanism. Said plainly because Story 8.6 shipped a comment crediting
 * `zIndex` with winning an overlap it could not win.
 *
 * ⚠️ PRESENCE IS THE SIGNAL, NEVER COLOUR (UX-DR8, WCAG 1.4.1). `UnreadDot`
 * renders nothing when there is nothing unread; there is no "read" dot and no
 * hue change. The accessible label carries the state as words, on the
 * wrapper; the dot itself stays hidden from assistive technology, as it is
 * everywhere. No count, anywhere: PRD r8 withdrew quantities.
 *
 * ⚠️ THE MENTION `@` SITS IN FLOW TO THE GLYPH'S LEFT (#1599 Story 1.5; owner
 * ruling 2026-10-22 after the first walk replaced frame B1's top-left
 * overhang, which was hard to read). A 14px bold `@` in `colors.error`, the
 * dot's own red ("glyphs match the dot"), drawn only while
 * `hasUnreadMention === true`, in a fixed `MENTION_SLOT_WIDTH` box that
 * `ACTIONS_COLUMN_MIN_WIDTH` reserves. It is a glyph, so it owes 3:1
 * (SC 1.4.11), and `error` measures 3.55:1 on white (`colors.test.ts`). Hidden
 * from assistive tech like the dot; the label says "mentions you" BEFORE
 * "unread", the B legend's order, built by one function (`discussionMarkerLabel`) so the
 * four forms cannot drift.
 *
 * ⚠️ THAT LABEL DOES NOT REACH A SCREEN READER ON THE NATIVE CARD, and this
 * component cannot fix it alone. `ViewRequests` renders the card as a
 * `TouchableOpacity`, which defaults to `accessible`, so iOS and Android treat
 * the whole card as one element and hide every descendant label, this one and
 * Copy's alike. #1599 Story 3.2 fixed both row actions together, on the card:
 * the card's own label appends this marker's words (`discussionMarkerLabel`,
 * the image form, so the card and the marker cannot drift), and "Open
 * discussion" and "Copy request" are the card's `accessibilityActions`. The
 * card stays ONE swipe stop; `accessible={false}` was rejected because every
 * card would become a dozen. On the web table the label is reached normally.
 */

import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { UnreadDot } from './UnreadDot';
import { colors } from '../../theme/colors';
import { MessageSquare } from '../icons';

import type { GestureResponderEvent, StyleProp, ViewStyle } from 'react-native';

interface Props {
  /**
   * `true` draws the glyph. `false` and `undefined` both draw nothing — the
   * distinction (computed-empty versus not-computed) is the server's to log
   * and never the row's to show.
   */
  hasDiscussion?: boolean;
  /** `true` adds the dot. Ignored unless `hasDiscussion` is `true`. */
  hasUnread?: boolean;
  /**
   * #1599 Story 1.5: `true` adds the red `@` at the glyph's top-left. Ignored
   * unless `hasDiscussion` is `true`; `false` and `undefined` draw nothing.
   */
  hasUnreadMention?: boolean;
  /**
   * Makes the marker a button that opens the discussion. The screen owns the
   * navigation AND must stop the event reaching the row (`event.stopPropagation`
   * on web), exactly as `handleCopyRequest` does for Copy.
   */
  onPress?: (event: GestureResponderEvent) => void;
  /** The action's chrome (padding, radius), so it lines up with Copy. */
  style?: StyleProp<ViewStyle>;
  /** The dot is `${testID}-unread`; the mention `@` is `${testID}-mention`. */
  testID?: string;
}

/** Matches the Copy action beside it on both renderers. */
const GLYPH_SIZE = 16;

/**
 * How far the dot is pulled past the glyph's top and right edges.
 *
 * Replaced the exported `GLYPH_TO_DOT_GAP = 4` on 2026-09-23, when the dot left
 * the flow: there is no longer a gap to size, and the number that matters is the
 * OVERHANG. Exported for the one guard that can now fail --
 * `view-requests-actions-column-floor.test.ts` asserts these stay within the
 * smaller of the two host paddings (`cardActionIcon`'s 6), because an overhang
 * at or past the padding reaches the rounded edge Android clips at.
 *
 * ⚠️ THE TWO AXES ARE NOT THE SAME NUMBER, and that is the owner's eye, not an
 * oversight: both started at 2 and were set to 3 / 5 from the browser on
 * 2026-09-23. Do not "tidy" them back into one constant.
 *
 * ⚠️ `RIGHT` HAS ONE POINT OF ROOM LEFT. The card renderer pads by 6
 * (`cardActionIcon`), so a 5 px overhang clears the rounded edge by 1 px. The
 * web table pads by `spacing.sm` (8) and is not close. Anyone raising this
 * number, or lowering `cardActionIcon`'s padding, is one point from clipping the
 * dot on Android -- which is why the guard compares against the TIGHTER host and
 * not against whichever one is being edited at the time.
 */
export const DOT_OVERHANG_TOP = 3;
export const DOT_OVERHANG_RIGHT = 5;

/**
 * The mention `@` (#1599 Story 1.5), IN FLOW to the LEFT of the glyph.
 *
 * ⚠️ OWNER RULING 2026-10-22, AFTER THE FIRST WALK: frame B1's overhang (a
 * 12px `@` pulled 5 past the glyph's top-left, `MENTION_OVERHANG_LEFT`) was
 * "hard to read" squeezed against the icon and its dot, and the owner asked
 * for it "next to the message icon when needed". So it is a sibling of the
 * glyph in a row, not an overlay: a fixed `MENTION_SLOT_WIDTH` box (so the
 * floor is a number, not a font measurement) and `MENTION_TO_GLYPH_GAP`
 * before the glyph. It therefore COSTS WIDTH when lit, and
 * `ACTIONS_COLUMN_MIN_WIDTH` reserves it (`ViewRequests.tsx`): the cell is
 * right-aligned, so the icon and Copy stay where they are and the `@` appears
 * to their left. Glyph icon at 3:1 (SC 1.4.11), `colors.error`, the dot's red
 * (owner ruling 2026-10-22).
 */
export const MENTION_SLOT_WIDTH = 12;
export const MENTION_TO_GLYPH_GAP = 2;

/**
 * The marker's spoken state: mention before unread (the B legend's order).
 * Exported (#1599 Story 3.2) so the native request card folds the SAME words
 * into its own label; there is one builder, so the two cannot drift.
 */
export const discussionMarkerLabel = (
  form: 'button' | 'image',
  unread: boolean,
  mention: boolean,
): string =>
  [
    form === 'button' ? 'Open discussion' : 'Has discussion',
    mention ? 'mentions you' : null,
    unread ? 'unread' : null,
  ]
    .filter(Boolean)
    .join(', ');

export const DiscussionRowIndicator: React.FC<Props> = ({
  hasDiscussion,
  hasUnread,
  hasUnreadMention,
  onPress,
  style,
  testID = 'discussion-row-indicator',
}) => {
  if (hasDiscussion !== true) return null;
  const unread = hasUnread === true;
  const mention = hasUnreadMention === true;
  const glyph = (
    <View style={styles.glyphBox}>
      <MessageSquare size={GLYPH_SIZE} color={colors.textSecondary} />
      <UnreadDot
        unread={unread}
        style={styles.dot}
        testID={`${testID}-unread`}
      />
    </View>
  );
  const content = mention ? (
    <View style={styles.mentionRow} testID={`${testID}-mention-row`}>
      <Text
        style={styles.mention}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        testID={`${testID}-mention`}
      >
        @
      </Text>
      {glyph}
    </View>
  ) : (
    glyph
  );
  if (onPress) {
    return (
      <TouchableOpacity
        style={[styles.wrap, style]}
        testID={testID}
        onPress={onPress}
        accessible
        accessibilityRole="button"
        accessibilityLabel={discussionMarkerLabel('button', unread, mention)}
        accessibilityHint="Opens the request with its discussion showing"
      >
        {content}
      </TouchableOpacity>
    );
  }
  return (
    <View
      style={[styles.wrap, style]}
      testID={testID}
      accessible
      accessibilityRole="image"
      accessibilityLabel={discussionMarkerLabel('image', unread, mention)}
    >
      {content}
    </View>
  );
};

const styles = StyleSheet.create({
  /**
   * The marker itself. It takes the caller's chrome (padding AND radius), which
   * is why it is NOT the box the dot is positioned against -- see the header.
   */
  wrap: {
    alignItems: 'center',
    justifyContent: 'center',
    /**
     * ⚠️ A FLOOR FOR THE FORM THAT HAS NO CALLER CHROME (review, 2026-09-23).
     * `style` is optional and the image form exists "for any surface that has
     * nowhere to go", so the safety argument in the header -- which is about
     * `actionButton`'s 8 and `cardActionIcon`'s 6 -- says nothing about a
     * caller that passes neither. Without this, the dot is pulled past a
     * zero-padding box and sits OUTSIDE the marker's own bounds, where an
     * ancestor's `overflow` or radius can cut it. Both real callers override
     * this with more (8 and 6), so it changes nothing on the list.
     */
    padding: DOT_OVERHANG_RIGHT,
  },
  /**
   * The POSITIONING CONTEXT, and that is all it is: the dot's offsets resolve
   * against the 16 px glyph rather than the 32 px button. It does NOT protect
   * the overhang from Android clipping -- it sits inside the rounded touchable,
   * so a clip would take it too. See the header; that claim was made here once
   * and was false. The padding is the protection.
   */
  glyphBox: {
    position: 'relative',
  },
  /**
   * Top-right of the glyph, where users already expect an unread badge. It
   * overhangs by 3 of its 12 points upward and 5 across -- NOT "half", which is
   * the language this comment was first copied from `DiscussionToggle` with; a
   * 12 px dot on a 16 px glyph occludes roughly its top-right 7x9 px. Out of
   * flow, so the read and unread markers are the same width. `zIndex` is
   * belt-and-braces: document order already paints it above (see the header).
   */
  dot: {
    position: 'absolute',
    top: -DOT_OVERHANG_TOP,
    right: -DOT_OVERHANG_RIGHT,
    zIndex: 1,
  },
  /**
   * #1599 Story 1.5: the `@` and the glyph side by side, only when lit (an
   * unmentioned marker is the bare glyph box, as before). See
   * `MENTION_SLOT_WIDTH` for the owner ruling that put it in flow.
   */
  mentionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: MENTION_TO_GLYPH_GAP,
  },
  /**
   * 14px bold on a 16-point line, the glyph's height, so the two centre on
   * one axis. A fixed width so the column floor can reserve it. A style
   * contract only: geometry is walk-verified (`correspondence.md`).
   */
  mention: {
    width: MENTION_SLOT_WIDTH,
    textAlign: 'center',
    fontSize: 14,
    lineHeight: GLYPH_SIZE,
    fontWeight: '700',
    color: colors.error,
  },
});
